var _excluded = ["isAnchor", "aria-label", "isBlock", "isCondensed", "type", "children", "disabled", "href", "onClick", "hasIconOnly", "isLoading", "isPrimary", "isHollow", "isInverse", "size", "shape", "readOnly", "className", "variant", "utils"];
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var s = Object.getOwnPropertySymbols(e); for (r = 0; r < s.length; r++) o = s[r], t.includes(o) || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.includes(n)) continue; t[n] = r[n]; } return t; }
import React from 'react';
import PropTypes from 'prop-types';
import StyledButton from './button-new.styles';
import Loader from '../loader';
import InverseContext from '../../utilities/inverse-context';
import { StyledCacheWrapper } from '../../styles';
import withDeprecatedProps from '../../utilities/with-deprecated-props/withDeprecatedProps';
var buttonPropTypes = {
  /** Change the html element to an <a /> tag. This will disable the onClick/type props in favor of href */
  isAnchor: PropTypes.bool,
  /** Aria label for button */
  'aria-label': PropTypes.string,
  /**
   * @deprecated since 2.56.0
   * @see aria-label
   */
  ariaLabel: PropTypes.string,
  /** Display the button as a block element */
  isBlock: PropTypes.bool,
  /** The buttons content */
  children: PropTypes.node,
  /** Add custom css classes */
  className: PropTypes.string,
  /** Reduced padding on button */
  isCondensed: PropTypes.bool,
  /** Disable the button */
  disabled: PropTypes.bool,
  /** The buttons type */
  type: PropTypes.oneOf(['button', 'submit', 'reset']),
  /** Add if the button has a variant that isn't 'icon' and only contains an icon element as children */
  hasIconOnly: PropTypes.bool,
  /** Add a loading state to the button. Will hide the content and show a Loader  */
  isLoading: PropTypes.bool,
  /** Adds emphasis to the variant color: Set to false for secondary buttons */
  isPrimary: PropTypes.bool,
  /** Controls the inverse style of the button */
  isInverse: PropTypes.oneOf(['auto', true, false]),
  /** The hollow boolean will remove the border/background color */
  isHollow: PropTypes.bool,
  /** The buttons onClick event */
  onClick: PropTypes.func,
  /** Url of the anchor tag (only if isAnchor={true}) */
  href: PropTypes.string,
  /** Read only state of the button */
  readOnly: PropTypes.bool,
  /** Button Shape - fillet: 8px rounded corners, pill: fully rounded at each end, square: square corners. */
  shape: PropTypes.oneOf(['fillet', 'pill', 'square']),
  /** the buttons size (t-shirt sizing) */
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  /** The buttons color variant */
  variant: PropTypes.oneOf(['community', 'success', 'information', 'protection', 'critical', 'caution', 'muted', 'phony']),
  /** Add utility props for styling */
  utils: PropTypes.object
};
var buttonDefaultProps = {
  isAnchor: false,
  'aria-label': null,
  ariaLabel: null,
  isCondensed: false,
  className: '',
  href: '#',
  type: 'button',
  children: 'My Button',
  disabled: false,
  onClick: function onClick() {},
  shape: 'pill',
  size: 'md',
  isBlock: false,
  readOnly: false,
  isLoading: false,
  isPrimary: true,
  isInverse: 'auto',
  hasIconOnly: false,
  isHollow: false,
  variant: 'information',
  utils: {}
};
var loaderSize = function loaderSize(size) {
  switch (size) {
    case 'lg':
      return;
    case 'sm':
      return 'xs';
    case 'xs':
      return 'xs';
    default:
      return;
  }
};

/**
 *
 * @ignore @description
 * ## Button Component
 * Buttons allow users to take action and should not be used as general navigation or to support browsing. Use a Button on a page to help the user execute an action. The different Button types are used for different purposes and may be used in combination on the same page.
 *
 * @ignore @see Mesh: [Button Docs](https://mesh-component-library.apps.csts-k8cnp-ro-1.r-caas.allstate.com/?path=/story/forms-button--default)
 *
 * @ignore @example
 * ```jsx
 * import { Button } from 'mesh-component-library';
 *
 * <Button {...props}>
 *  {children}
 * </Button>
 * ```
 * @type React.ForwardRefRenderFunction<React.FunctionComponent, buttonPropTypes>
 */
export var Button = /*#__PURE__*/React.forwardRef(function (_ref, forwardedRef) {
  var isAnchor = _ref.isAnchor,
    ariaLabel = _ref['aria-label'],
    isBlock = _ref.isBlock,
    isCondensed = _ref.isCondensed,
    type = _ref.type,
    children = _ref.children,
    disabled = _ref.disabled,
    href = _ref.href,
    onClick = _ref.onClick,
    hasIconOnly = _ref.hasIconOnly,
    isLoading = _ref.isLoading,
    isPrimary = _ref.isPrimary,
    isHollow = _ref.isHollow,
    isInverse = _ref.isInverse,
    size = _ref.size,
    shape = _ref.shape,
    readOnly = _ref.readOnly,
    className = _ref.className,
    variant = _ref.variant,
    utils = _ref.utils,
    other = _objectWithoutProperties(_ref, _excluded);
  var content = isLoading ? /*#__PURE__*/React.createElement(Loader, {
    size: loaderSize(size)
  }) : children;
  var aria = isLoading ? 'loading' : ariaLabel;
  var isReadOnly = isLoading ? true : readOnly;
  var useIsInverse = React.useContext(InverseContext);
  if (isInverse !== 'auto') {
    useIsInverse = isInverse;
  }
  return /*#__PURE__*/React.createElement(StyledCacheWrapper, null, /*#__PURE__*/React.createElement(StyledButton, _extends({
    as: isAnchor ? 'a' : 'button',
    "aria-label": aria,
    isBlock: isBlock,
    className: className,
    isCondensed: isCondensed,
    disabled: disabled,
    hasIconOnly: hasIconOnly,
    href: isAnchor ? href : undefined,
    isInverse: useIsInverse,
    isPrimary: isPrimary,
    isHollow: isHollow,
    onClick: onClick,
    readOnly: isReadOnly,
    ref: forwardedRef,
    shape: shape,
    size: size,
    type: !isAnchor ? type : undefined,
    variant: variant,
    utils: utils
  }, other), content));
});
Button.propTypes = buttonPropTypes;
Button.displayName = 'Button';
Button.defaultProps = buttonDefaultProps;
/**
 * @type React.FC<buttonPropTypes>
 */
export default withDeprecatedProps(Button);